import {AfterViewInit, Directive, ElementRef, NgZone, OnDestroy} from '@angular/core';

/**
 * Enables auto-scroll on text selection in froala editor fields.
 * Applied on the .qa-fake-input container, it finds the .fr-element child
 * and scrolls it horizontally when the user drags a text selection beyond
 * the field boundaries.
 */
@Directive({
  selector: '[qaFroalaAutoScroll]'
})
export class FroalaAutoScrollDirective implements AfterViewInit, OnDestroy {
  private frElement: HTMLElement | null = null;
  private isDragging = false;
  private documentListenersAttached = false;
  private animationFrameId: number | null = null;
  private scrollSpeed = 0;
  private mouseDownHandler: (e: MouseEvent) => void = () => undefined;
  private mouseMoveHandler: (e: MouseEvent) => void = () => undefined;
  private mouseUpHandler: () => void = () => undefined;

  /**
   * Injects the host element reference
   */
  constructor(private el: ElementRef<HTMLElement>, private zone: NgZone) {}

  /**
   * Initializes the directive after the view is ready.
   * Uses a delay because froala renders .fr-element asynchronously.
   */
  public ngAfterViewInit(): void {
    setTimeout(() => this.init(), 200);
  }

  /**
   * Cleans up all event listeners and stops any running scroll loop
   */
  public ngOnDestroy(): void {
    this.stopScrollLoop();

    if (this.frElement) {
      this.frElement.removeEventListener('mousedown', this.mouseDownHandler);
    }

    this.detachDocumentListeners();
  }

  /**
   * Finds the .fr-element child and sets up event listeners
   */
  private init(): void {
    this.frElement = this.el.nativeElement.querySelector('.fr-element');

    if (!this.frElement) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.mouseDownHandler = () => this.onMouseDown();
      this.mouseMoveHandler = (e: MouseEvent) => this.onMouseMove(e);
      this.mouseUpHandler = () => this.onMouseUp();
      this.frElement.addEventListener('mousedown', this.mouseDownHandler);
    });
  }

  /**
   * Marks the start of a potential text selection drag
   */
  private onMouseDown(): void {
    this.isDragging = true;
    this.attachDocumentListeners();
  }

  /**
   * Calculates scroll direction and speed based on mouse position relative to the field
   */
  private onMouseMove(e: MouseEvent): void {
    if (!this.isDragging || !this.frElement) {
      return;
    }

    const rect = this.frElement.getBoundingClientRect();
    const edgeThreshold = 20;

    if (e.clientX < rect.left - edgeThreshold) {
      this.scrollSpeed = -Math.min(15, (rect.left - e.clientX) * 0.5);
      this.startScrollLoop();
    } else if (e.clientX > rect.right + edgeThreshold) {
      this.scrollSpeed = Math.min(15, (e.clientX - rect.right) * 0.5);
      this.startScrollLoop();
    } else if (e.clientX < rect.left + edgeThreshold) {
      this.scrollSpeed = -Math.min(8, (rect.left + edgeThreshold - e.clientX) * 0.3);
      this.startScrollLoop();
    } else if (e.clientX > rect.right - edgeThreshold) {
      this.scrollSpeed = Math.min(8, (e.clientX - rect.right + edgeThreshold) * 0.3);
      this.startScrollLoop();
    } else {
      this.stopScrollLoop();
    }
  }

  /**
   * Stops dragging and the scroll loop on mouse up
   */
  private onMouseUp(): void {
    this.isDragging = false;
    this.stopScrollLoop();
    this.detachDocumentListeners();
  }

  /**
   * Adds global listeners only while actively dragging.
   * This avoids hundreds of always-on document handlers for large questionnaires.
   */
  private attachDocumentListeners(): void {
    if (this.documentListenersAttached) {
      return;
    }

    this.documentListenersAttached = true;
    document.addEventListener('mousemove', this.mouseMoveHandler, {passive: true});
    document.addEventListener('mouseup', this.mouseUpHandler);
  }

  /**
   * Removes global listeners after drag ends.
   */
  private detachDocumentListeners(): void {
    if (!this.documentListenersAttached) {
      return;
    }

    this.documentListenersAttached = false;
    document.removeEventListener('mousemove', this.mouseMoveHandler);
    document.removeEventListener('mouseup', this.mouseUpHandler);
  }

  /**
   * Starts the requestAnimationFrame-based scroll loop if not already running
   */
  private startScrollLoop(): void {
    if (this.animationFrameId !== null) {
      return;
    }

    const scroll = () => {
      if (!this.frElement) {
        return;
      }

      this.frElement.scrollLeft += this.scrollSpeed;
      this.animationFrameId = requestAnimationFrame(scroll);
    };

    this.animationFrameId = requestAnimationFrame(scroll);
  }

  /**
   * Stops the scroll loop and resets scroll speed
   */
  private stopScrollLoop(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    this.scrollSpeed = 0;
  }
}
